import {
  Accordion,
  Button,
  CopyButton,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import {
  selectDrofusChangeDetails,
  type IChangeEventDetails,
} from "./drofusChangeDetailsSelectors";
import { valuePreview } from "./drofusChangePresentation";

interface IChangeEventDetailsProps {
  event: IChangeEventDetails;
}

const DetailsFields: React.FC<{
  fields: ReturnType<typeof selectDrofusChangeDetails>["identification"];
}> = ({ fields }) => (
  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
    {fields.map(([label, value]) => (
      <div key={label}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        <Text
          size="sm"
          style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", userSelect: "text" }}
        >
          {value == null || value === "" ? "Ikke registrert" : value}
        </Text>
      </div>
    ))}
  </SimpleGrid>
);

export const DrofusChangeEventMetadata: React.FC<IChangeEventDetailsProps> = ({ event }) => {
  const details = selectDrofusChangeDetails(event);
  return (
    <Stack gap="md">
      <DetailsFields fields={details.identification} />
      <Text fw={600}>Tidspunkt og sporbarhet</Text>
      <Text size="xs" c="dimmed">
        Innlesningsdatoen viser når endringen ble oppdaget. En tilknyttet dRofus-logg kan gjelde
        forekomsten generelt og er ikke nødvendigvis tidspunktet for dette feltet.
      </Text>
      <DetailsFields fields={details.timing} />
      <Accordion variant="contained">
        <Accordion.Item value="technical">
          <Accordion.Control>Tekniske detaljer og rådata</Accordion.Control>
          <Accordion.Panel>
            <Stack>
              <DetailsFields fields={details.technical} />
              <Group>
                <CopyButton value={details.raw}>
                  {({ copied, copy }) => (
                    <Button size="xs" variant="light" onClick={copy}>
                      {copied ? "Kopiert" : "Kopier endringsdata"}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Text
                component="pre"
                size="xs"
                style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", margin: 0 }}
              >
                {details.raw}
              </Text>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
};

export const DrofusChangeEventDetails: React.FC<IChangeEventDetailsProps> = ({ event }) => (
  <Stack>
    <Text size="lg" fw={700} style={{ overflowWrap: "anywhere" }}>
      {event.changeType === "updated" ? "Endret attributt: " : ""}
      {event.displayName ?? event.normalizedPath ?? event.changeKey}
    </Text>
    {(["oldValue", "newValue"] as const).map((side) => (
      <Paper key={side} p="sm" bg={side === "oldValue" ? "red.0" : "green.0"} radius="sm">
        <Text size="xs" fw={700}>
          {side === "oldValue" ? "Gammel verdi" : "Ny verdi"}
        </Text>
        <Text size="sm" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {valuePreview(event, side)}
        </Text>
      </Paper>
    ))}
    <DrofusChangeEventMetadata event={event} />
  </Stack>
);
